import React, { useState } from "react";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { MdAdd, MdLock } from "react-icons/md";

import CircularLoader from "../../../../../../../Components/CircularLoader/CircularLoader";
import { fetchSingleOrder } from "../../../../../../../services/orders.services";
import {
  addRunOrders,
  fetchRuns,
} from "../../../../../../../services/ShipperServices/DeliveryRoutes/deliveryRuns.services";
import { leftOutReason } from "./CreateRunModal";
import { deliveryAddress } from "./routeSheet";
import {
  formatDuration,
  formatKm,
  loopPoints,
  MAX_WAYPOINTS,
  routeTotals,
} from "./runDistance";
import { bestLoopTotals, insertionTotals, sortRun } from "./runOrder";
import {
  isLockedRun,
  isOpenRun,
  RUN_MAX_STOPS,
  runDay,
  runTitle,
  stopsText,
} from "./runs";

const routeIdOf = (row) => row?.route_id ?? row?.route?.id ?? null;

// Distances are worked out for the first few runs listed (each costs Google
// requests).
export const DISTANCE_RUNS = 5;

// The run's stop addresses in order (the runs list returns them with
// with_stops).
export const runStopAddresses = (run) =>
  [...(Array.isArray(run?.stops) ? run.stops : [])]
    .sort((a, b) => Number(a?.position) - Number(b?.position))
    .map((stop) => deliveryAddress(stop?.order?.order_shipping_address_details))
    .filter(Boolean);

// The order's full delivery address. The Orders list only has its city,
// state and country, so the order is read once when that's all there is.
export const orderAddress = async (row) => {
  const listed = deliveryAddress(row?.order_shipping_address_details);
  if (listed) return listed;
  const response = await fetchSingleOrder(row?.id);
  return response?.error
    ? ""
    : deliveryAddress(response?.data?.result?.order_shipping_address_details);
};

// The run's loop in its best order, now and with this order added (Add to
// run sorts the run again): { total, added } ({ meters, seconds } each, or
// null). With more stops than Google sorts at once, the loop as it is and
// the order where it fits best. When Google can't say, the loop as it is
// and with the order at the end.
export const runDistances = async (run, row, locations = []) => {
  // Servers without with_stops send no stops: nothing to measure.
  if (!Array.isArray(run?.stops)) return { total: null, added: null };
  const start =
    locations.find(
      (location) => String(location?.id) === String(run?.start_warehouse_id),
    )?.address || "";
  const stops = runStopAddresses(run);
  const own = await orderAddress(row);
  if (own && stops.length && stops.length + 1 > MAX_WAYPOINTS) {
    const fitted = await insertionTotals(start, stops, own);
    if (fitted?.total && fitted.added) return fitted;
  } else if (own && stops.length) {
    const [bestNow, bestWith] = await Promise.all([
      bestLoopTotals(start, stops),
      bestLoopTotals(start, [...stops, own]),
    ]);
    if (bestNow && bestWith) {
      return {
        total: bestNow,
        added: {
          meters: bestWith.meters - bestNow.meters,
          seconds: bestWith.seconds - bestNow.seconds,
        },
      };
    }
  }
  const [total, withOrder] = await Promise.all([
    routeTotals(loopPoints(start, stops)),
    own ? routeTotals(loopPoints(start, [...stops, own])) : null,
  ]);
  const added =
    total && withOrder
      ? {
          meters: withOrder.meters - total.meters,
          seconds: withOrder.seconds - total.seconds,
        }
      : !stops.length && withOrder
        ? withOrder
        : null;
  return { total, added };
};

// The open runs, those on the order's own route first (the list comes newest
// first and keeps that order otherwise).
export const runsForOrder = (runs, row) => {
  const routeId = routeIdOf(row);
  const open = runs.filter(isOpenRun);
  if (routeId == null) return open;
  const same = (run) =>
    String(run?.route_id ?? run?.route?.id) === String(routeId);
  return [...open.filter(same), ...open.filter((run) => !same(run))];
};

/**
 * Add to run, on an order with a route that isn't on an open run yet (and
 * isn't fulfilled or cancelled): opens the open runs, its own route's first,
 * adds the order to the one picked and sorts that run's stops for the
 * shortest drive again. A locked run is listed as locked and can't be picked.
 * `onAdded(run, sorting)` gets the run as saved and sortRun's result
 * ({ sorted, problem }); `onError(text)` hears a failed save. With
 * `onNewRun(row)`, "New run with #id" comes first, even while the runs load,
 * and hands the order over to start a run of its own.
 */
const AddRunMenu = ({ row, locations = [], onAdded, onError, onNewRun }) => {
  const [anchor, setAnchor] = useState(null);
  const [runs, setRuns] = useState([]);
  // { [runId]: "loading" | { total, added } }
  const [distances, setDistances] = useState({});
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  if (routeIdOf(row) == null || leftOutReason(row)) return null;

  const open = async (event) => {
    event.stopPropagation();
    setAnchor(event.currentTarget);
    setLoading(true);
    setFailed(false);
    const response = await fetchRuns("", { withStops: true });
    const list = response?.data?.result?.data;
    const ordered = Array.isArray(list) ? runsForOrder(list, row) : [];
    setFailed(Boolean(response.error) || !Array.isArray(list));
    setRuns(ordered);
    setLoading(false);
    // A locked run can't take the order: no Google requests for it.
    const measured = ordered
      .filter((run) => !isLockedRun(run))
      .slice(0, DISTANCE_RUNS);
    setDistances(
      Object.fromEntries(measured.map((run) => [run.id, "loading"])),
    );
    measured.forEach((run) => {
      runDistances(run, row, locations).then((result) =>
        setDistances((current) => ({ ...current, [run.id]: result })),
      );
    });
  };

  const distanceText = (run) => {
    const known = distances[run.id];
    if (known === "loading") return "Working out km and time…";
    if (!known?.total && !known?.added) return "";
    return [
      known.total
        ? `${formatKm(known.total.meters)} · ${formatDuration(known.total.seconds)}`
        : "",
      known.added
        ? `${formatKm(known.added.meters, true)} · ${formatDuration(known.added.seconds, true)} with #${row?.id}`
        : "",
    ]
      .filter(Boolean)
      .join(" · ");
  };

  const add = async (run) => {
    setAnchor(null);
    setSaving(true);
    try {
      const response = await addRunOrders(run.id, [row.id]);
      const saved = response.error ? null : response?.data?.result;
      if (saved?.id == null) {
        onError?.(
          response.error?.response?.data?.message ||
            "Couldn't add the order to the run. Try again.",
        );
        return;
      }
      const sorting = await sortRun(saved, locations);
      onAdded?.(sorting.run, sorting);
    } finally {
      setSaving(false);
    }
  };

  const newRun = () => {
    setAnchor(null);
    onNewRun(row);
  };

  const ownRoute = (run) =>
    String(run?.route_id ?? run?.route?.id) === String(routeIdOf(row));

  return (
    <>
      <button
        type="button"
        onClick={open}
        disabled={saving}
        aria-busy={saving}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        aria-label={`Add #${row?.id} to a run`}
        className="flex shrink-0 items-center gap-[3px] whitespace-nowrap border-0 bg-transparent p-0 font-medium text-brand hover:underline disabled:cursor-wait disabled:opacity-50"
      >
        <MdAdd className="h-[12px] w-[12px] shrink-0" />
        Add to run
      </button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(event) => event.stopPropagation()}
        MenuListProps={{ dense: true, "aria-label": "Open runs" }}
        PaperProps={{
          sx: {
            minWidth: 220,
            maxHeight: 320,
            "& .MuiMenuItem-root": {
              minHeight: "28px",
              padding: "4px 10px",
              fontSize: 12,
            },
          },
        }}
      >
        {onNewRun && (
          <MenuItem onClick={newRun}>
            <span className="grid min-w-[0px] gap-[1px]">
              <span className="flex items-center gap-[4px] font-semibold text-brand">
                <MdAdd
                  aria-hidden="true"
                  className="h-[13px] w-[13px] shrink-0"
                />
                New run with #{row?.id}
              </span>
              <span className="truncate text-[11px] text-ui-text-muted">
                Pick its date, start and driver
              </span>
            </span>
          </MenuItem>
        )}
        {onNewRun && <Divider sx={{ my: "4px !important" }} />}
        {loading ? (
          <MenuItem disabled>
            <span className="flex items-center gap-[8px]">
              <CircularLoader size={14} />
              Loading the runs…
            </span>
          </MenuItem>
        ) : failed ? (
          <MenuItem disabled>Couldn't load the runs. Try again.</MenuItem>
        ) : runs.length === 0 ? (
          <MenuItem disabled>
            {onNewRun
              ? "No open runs yet."
              : "No open runs. Tick orders and use Create run."}
          </MenuItem>
        ) : (
          runs.map((run) => {
            const full = Number(run.stops_count ?? 0) >= RUN_MAX_STOPS;
            const locked = isLockedRun(run);
            return (
              <MenuItem
                key={run.id}
                disabled={full || locked}
                onClick={full || locked ? undefined : () => add(run)}
              >
                <span className="grid min-w-[0px] gap-[1px]">
                  <span className="truncate font-semibold">
                    {runTitle(run)}
                    {ownRoute(run) ? "" : " · other route"}
                  </span>
                  <span className="truncate text-[11px] text-ui-text-muted">
                    {[
                      runDay(run.run_date),
                      stopsText(run.stops_count),
                      full ? "full" : "",
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  {locked && (
                    <span className="flex min-w-[0px] items-center gap-[4px] text-[11px] font-medium text-state-warning-text">
                      <MdLock
                        aria-hidden="true"
                        className="h-[11px] w-[11px] shrink-0"
                      />
                      <span className="truncate">
                        Locked: unlock it to add orders
                      </span>
                    </span>
                  )}
                  {distanceText(run) && (
                    <span className="truncate text-[11px] font-medium text-ui-text">
                      {distanceText(run)}
                    </span>
                  )}
                </span>
              </MenuItem>
            );
          })
        )}
      </Menu>
    </>
  );
};

export default AddRunMenu;
